import { CallbackListener } from '@remotion/player';
import { useCallback, useContext, useEffect, useState } from 'react';
import { PlayerRefContext } from '../context-provider';

/**
 * 현재 플레이어 프레임을 구독한다 — Remotion Player 의 `frameupdate` 이벤트로 갱신.
 *
 * use-timeline-position 의 구독 패턴을 공용화한 것으로, playerRef 를 인자로 받지 않고
 * PlayerRefContext 에서 직접 읽는다. 이 훅을 쓰는 컴포넌트만 매 프레임 리렌더되도록
 * memo 경계 안에 두고 사용한다(상위 컴포넌트로 프레임 갱신 전파 금지).
 */
export const useCurrentPlayerFrame = (): number => {
  const playerRef = useContext(PlayerRefContext);
  const [frame, setFrame] = useState(
    () => playerRef?.current?.getCurrentFrame() ?? 0,
  );

  const handleFrameUpdate: CallbackListener<'frameupdate'> = useCallback(
    (opts) => {
      setFrame(opts.detail.frame);
    },
    [],
  );

  useEffect(() => {
    const current = playerRef?.current;
    if (!current) {
      return;
    }
    current.addEventListener('frameupdate', handleFrameUpdate);
    return () => {
      current.removeEventListener('frameupdate', handleFrameUpdate);
    };
  }, [handleFrameUpdate, playerRef]);

  return frame;
};
